// Tours (F11): index, lazy stop loading and the player actions.
import { batch } from '@preact/signals';
import index from '../generated/tours.json';
import * as st from '../state/store';
import { initialLayers, type LayerId, type LayerState } from '../viewer/layers';
import { fromUrl } from '../state/url';
import { canAccess } from '../entitlements/tier';
import { BY_ID } from '../state/structures';
import { track } from '../analytics';
import type { CameraState } from '../viewer/Viewer';

export interface TourStop {
  title: string;
  text: string;
  focus: string;
  highlight: string[];
  layers?: Partial<Record<LayerId, LayerState>>;
  isolate?: 'alone' | 'skeleton' | 'region';
  view?: string;
}
export interface TourSummary {
  id: string;
  title: string;
  summary: string;
  tier: 'free' | 'supporter' | 'school';
  audience: string[];
  curriculumTags: string[];
  yearLevels: string | null;
  minutes: number;
  sponsor: { name: string; url: string } | null;
  stops: Array<{ title: string; text?: string }>;
}
export const TOURS = index as TourSummary[];
export const TOUR_BY_ID = new Map(TOURS.map((t) => [t.id, t]));

const loaded = new Map<string, Promise<TourStop[] | null>>();
export function loadStops(id: string): Promise<TourStop[] | null> {
  if (!loaded.has(id))
    loaded.set(id, fetch(`/content/tours/${id}.json`).then((r) => (r.ok ? r.json() : null)).then((j) => (j ? (j.stops as TourStop[]) : null)).catch(() => null));
  return loaded.get(id)!;
}

/** Stops a viewer may see: all for unlocked tours, the first one as a preview otherwise. */
export const visibleStops = (t: TourSummary) => (canAccess(t.tier) ? t.stops.length : 1);

/** Applies a stop's view to the store; returns the camera to use (or null to frame `focus`). */
export function applyStop(stop: TourStop): CameraState | null {
  const view = stop.view ? fromUrl('/', `?${stop.view}`) : null;
  // A stop about a part only one figure has shows that figure (F22: most tours were written on the male body).
  const only = BY_ID.get(stop.focus)?.body;
  if (only && only !== st.body.value) st.setBody(only, { remember: false });
  batch(() => {
    st.layers.value = { ...initialLayers(), ...(view?.layers ?? {}), ...(stop.layers ?? {}) };
    st.hidden.value = view?.hidden ?? [];
    st.isolate.value = stop.isolate ? { mode: stop.isolate, ids: [stop.focus, ...stop.highlight.filter((h) => h !== stop.focus)] } : view?.isolate ?? null;
    st.highlight.value = stop.highlight.length ? stop.highlight : [stop.focus];
    st.selected.value = null;
  });
  return view?.camera ?? null;
}

export function startTour(id: string, stop = 0) {
  const t = TOUR_BY_ID.get(id);
  if (!t) return;
  st.tour.value = { id, stop: Math.min(stop, t.stops.length - 1) };
  st.panel.value = 'tours';
  if (stop === 0) track('tour_start', { tour_id: id });
}

export function gotoStop(n: number) {
  const cur = st.tour.value;
  const t = cur && TOUR_BY_ID.get(cur.id);
  if (!cur || !t) return;
  const max = visibleStops(t) - 1;
  const next = Math.max(0, Math.min(n, t.stops.length - 1));
  if (next > max) return; // locked
  st.tour.value = { id: cur.id, stop: next };
  if (next === t.stops.length - 1) track('tour_complete', { tour_id: cur.id });
}

export function exitTour() {
  batch(() => {
    st.tour.value = null;
    st.highlight.value = [];
    st.panel.value = null;
  });
}
